import { useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import { cn } from '@/lib/cn';
import {
  akeys, fetchSummary, fmtInt, fmtMoney, fmtPct, fmtX, PLATFORM_LABEL, RESULT_LABEL,
  type OrganicBlock, type PaidBlock, type Scope, type Show, type Summary, type SummaryParams,
} from '@/lib/analytics';

/** The analytics of one clinic, one strategy or one piece (docs/ARCHITECTURE-ANALYTICS.md).
 *  Organic and paid are two blocks, each with its own numbers; only the final totals sit together,
 *  split paid / organic / no source. Every rate names its denominator. Numbers come from one
 *  endpoint, so they agree everywhere on the page. */

const ORG = 'var(--data-organic)';
const PAID = 'var(--data-paid)';
const PLATFORMS = ['instagram', 'facebook', 'tiktok', 'snapchat'] as const;
const today = () => new Date(Date.now() + 4 * 3600_000).toISOString().slice(0, 10);   // Dubai day
const daysAgo = (n: number) => new Date(Date.now() + 4 * 3600_000 - n * 86_400_000).toISOString().slice(0, 10);
const monthStart = () => today().slice(0, 8) + '01';

type Range = '7' | '30' | '90' | 'month' | 'since' | 'custom';

export default function AnalyticsView({ scope, id, postedSince, compact }: {
  scope: Scope; id: string;
  /** a piece: dates default to "since posted" */
  postedSince?: string | null;
  compact?: boolean;
}) {
  const [range, setRange] = useState<Range>(scope === 'creative' && postedSince ? 'since' : '30');
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [show, setShow] = useState<Show>('both');
  const [platforms, setPlatforms] = useState<string[]>([...PLATFORMS]);
  const [stage, setStage] = useState('');
  const [ctype, setCtype] = useState('');

  const win = useMemo(() => {
    switch (range) {
      case '7': return { from: daysAgo(6), to: today() };
      case '90': return { from: daysAgo(89), to: today() };
      case 'month': return { from: monthStart(), to: today() };
      case 'since': return { from: (postedSince || daysAgo(29)).slice(0, 10), to: today() };
      case 'custom': return { from, to };
      default: return { from: daysAgo(29), to: today() };
    }
  }, [range, from, to, postedSince]);

  const params: SummaryParams = {
    scope, id, ...win, show, stage: stage || undefined, type: ctype || undefined,
    platforms: platforms.length === PLATFORMS.length ? undefined : platforms,
  };
  const q = useQuery({ queryKey: akeys.summary(params), queryFn: () => fetchSummary(params), placeholderData: keepPreviousData });
  const s = q.data;

  const clearAll = () => { setRange(scope === 'creative' && postedSince ? 'since' : '30'); setShow('both'); setPlatforms([...PLATFORMS]); setStage(''); setCtype(''); };
  const applied: { label: string; clear?: () => void }[] = [
    { label: `${win.from} to ${win.to}` },
    ...(show !== 'both' ? [{ label: show === 'organic' ? 'Organic only' : 'Paid only', clear: () => setShow('both') }] : []),
    ...(platforms.length !== PLATFORMS.length ? [{ label: platforms.length ? platforms.map((p) => PLATFORM_LABEL[p] ?? p).join(', ') : 'No platform', clear: () => setPlatforms([...PLATFORMS]) }] : []),
    ...(stage ? [{ label: `Stage ${stage}`, clear: () => setStage('') }] : []),
    ...(ctype ? [{ label: ctype, clear: () => setCtype('') }] : []),
  ];

  return (
    <div className="flex flex-col gap-4" data-testid={`analytics-${scope}`}>
      {/* ---- filters: one bar, above everything it scopes */}
      <Card className="p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-4">
          {scope !== 'clinic' && (
            <div className="flex flex-col gap-1"><span className="text-[11px] text-muted">Scope</span>
              <span className="h-8 inline-flex items-center px-3 rounded-full bg-accent-600/15 text-accent-400 text-xs font-medium" data-testid="analytics-scope">
                {scope === 'creative' ? 'This piece only' : 'This strategy only'}</span></div>
          )}
          <div className="flex flex-col gap-1"><span className="text-[11px] text-muted">Dates</span>
            <div className="flex flex-wrap gap-1">
              {([...(scope === 'creative' && postedSince ? [['since', 'Since posted']] : []), ['7', '7 days'], ['30', '30 days'], ['90', '90 days'], ['month', 'This month'], ['custom', 'Custom']] as [Range, string][]).map(([k, l]) => (
                <Seg key={k} on={range === k} onClick={() => setRange(k)}>{l}</Seg>
              ))}
            </div>
          </div>
          {range === 'custom' && (
            <div className="flex items-end gap-2">
              <label className="flex flex-col gap-1 text-[11px] text-muted">From<input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
                className="h-8 rounded-lg border border-line bg-bg px-2 text-xs text-muted-strong" /></label>
              <label className="flex flex-col gap-1 text-[11px] text-muted">To<input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)}
                className="h-8 rounded-lg border border-line bg-bg px-2 text-xs text-muted-strong" /></label>
            </div>
          )}
          <div className="flex flex-col gap-1"><span className="text-[11px] text-muted">Show</span>
            <div className="flex gap-1">
              {(['both', 'organic', 'paid'] as Show[]).map((k) => (
                <Seg key={k} on={show === k} tone={k === 'organic' ? ORG : k === 'paid' ? PAID : undefined} onClick={() => setShow(k)} testid={`show-${k}`}>
                  {k === 'both' ? 'Both' : k === 'organic' ? 'Organic' : 'Paid'}</Seg>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1"><span className="text-[11px] text-muted">Platforms</span>
            <div className="flex flex-wrap gap-1">
              {PLATFORMS.map((p) => {
                const on = platforms.includes(p);
                return <button key={p} type="button" aria-pressed={on} onClick={() => setPlatforms((x) => (on ? x.filter((y) => y !== p) : [...x, p]))}
                  className={cn('h-8 px-3 rounded-full text-xs border transition', on ? 'bg-accent-600/15 text-accent-400 border-accent-500/50' : 'text-muted border-line')}>
                  {PLATFORM_LABEL[p] ?? p}</button>;
              })}
            </div>
          </div>
          {scope !== 'creative' && (
            <>
              <div className="w-40"><Select size="sm" label="Strategy stage" value={stage} onChange={setStage}
                options={[{ value: '', label: 'All stages' }, { value: 'TOF', label: 'Top (TOF)' }, { value: 'MOF', label: 'Middle (MOF)' }, { value: 'BOF', label: 'Bottom (BOF)' }]} /></div>
              <div className="w-36"><Select size="sm" label="Type" value={ctype} onChange={setCtype}
                options={[{ value: '', label: 'All types' }, { value: 'reel', label: 'Reel' }, { value: 'video', label: 'Video' }, { value: 'static', label: 'Static' }, { value: 'carousel', label: 'Carousel' }, { value: 'story', label: 'Story' }]} /></div>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 border-t border-line pt-3">
          <span className="text-[11px] text-muted mr-1">Applied</span>
          {applied.map((a) => (
            <span key={a.label} className="h-7 inline-flex items-center gap-1.5 px-2.5 rounded-full border border-line text-xs text-muted-strong">
              {a.label}{a.clear && <button type="button" aria-label={`Remove ${a.label}`} onClick={a.clear} className="text-muted hover:text-muted-strong">×</button>}
            </span>
          ))}
          <button type="button" onClick={clearAll} className="h-7 px-2 text-xs text-accent-400 hover:underline">Clear all</button>
        </div>
      </Card>

      {q.isError && <Card><p className="text-sm text-red-400">Could not load the analytics.</p></Card>}
      {!s && q.isLoading && <Card><p className="text-sm text-muted">Loading the numbers...</p></Card>}
      {s && (
        <div className={cn('flex flex-col gap-4 transition-opacity', q.isFetching && 'opacity-60')}>
          <Results s={s} />
          {s.plan && <Plan plan={s.plan} />}
          {s.organic && <Organic s={s} scope={scope} />}
          {s.paid && <Paid s={s} />}
          {scope !== 'creative' && <Pieces s={s} compact={compact} />}
          <Sources s={s} />
        </div>
      )}
    </div>
  );
}

function Seg({ on, onClick, children, tone, testid }: { on: boolean; onClick: () => void; children: ReactNode; tone?: string; testid?: string }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} data-testid={testid}
      style={on && tone ? { background: tone, borderColor: tone } : undefined}
      className={cn('h-8 px-3 rounded-lg text-xs border transition', on ? (tone ? 'text-white' : 'bg-accent-600 text-white border-accent-600') : 'border-line text-muted hover:text-muted-strong')}>
      {children}
    </button>
  );
}

function Tile({ label, value, note, wrap }: { label: string; value: string; note?: string; wrap?: boolean }) {
  return (
    <div className="rounded-lg bg-bg/60 border border-line px-3 py-2.5 min-w-0">
      <div className="text-[11px] text-muted">{label}</div>
      <div className={cn('font-semibold text-muted-strong mt-0.5', wrap ? 'text-sm leading-snug' : 'text-lg truncate')}>{value}</div>
      {note && <div className={cn('text-[11px] text-muted mt-0.5', !wrap && 'truncate')} title={note}>{note}</div>}
    </div>
  );
}

function Block({ tone, title, subtitle, children, testid }: { tone: string; title: string; subtitle: string; children: ReactNode; testid: string }) {
  return (
    <section className="rounded-2xl border p-4 md:p-5 flex flex-col gap-4" style={{ borderColor: `color-mix(in srgb, ${tone} 45%, transparent)`, background: `color-mix(in srgb, ${tone} 6%, transparent)` }} data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-3.5 h-3.5 rounded" style={{ background: tone }} aria-hidden="true" />
        <h2 className="text-lg font-bold tracking-wide text-muted-strong">{title}</h2>
        <span className="text-xs text-muted">{subtitle}</span>
      </div>
      {children}
    </section>
  );
}

function Results({ s }: { s: Summary }) {
  const r = s.results;
  const split = (k: 'leads' | 'revenue') => {
    const total = r.split.paid[k] + r.split.organic[k] + r.split.none[k];
    const w = (v: number) => (total ? `${(v / total) * 100}%` : '0%');
    return (
      <>
        <div className="flex gap-0.5 h-2 rounded overflow-hidden bg-slate-700/30" role="img"
          aria-label={`Paid ${r.split.paid[k]}, organic ${r.split.organic[k]}, no source ${r.split.none[k]}`}>
          <div style={{ width: w(r.split.paid[k]), background: PAID }} /><div style={{ width: w(r.split.organic[k]), background: ORG }} />
          <div style={{ width: w(r.split.none[k]) }} className="bg-slate-500/60" />
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted">
          <span><i className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: PAID }} />Paid {k === 'revenue' ? fmtMoney(r.split.paid[k], r.currency) : fmtInt(r.split.paid[k])}</span>
          <span><i className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: ORG }} />Organic {k === 'revenue' ? fmtMoney(r.split.organic[k], r.currency) : fmtInt(r.split.organic[k])}</span>
          <span><i className="inline-block w-2 h-2 rounded-sm mr-1 bg-slate-500/60" />No source {k === 'revenue' ? fmtMoney(r.split.none[k], r.currency) : fmtInt(r.split.none[k])}</span>
        </div>
      </>
    );
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="analytics-results">
      <Card className="p-4 flex flex-col gap-2"><div className="text-xs text-muted">Revenue (CRM): purchases recorded, by the lead's arrival day</div>
        <div className="text-2xl font-semibold text-muted-strong">{fmtMoney(r.revenue, r.currency)}</div>{split('revenue')}</Card>
      <Card className="p-4 flex flex-col gap-2"><div className="text-xs text-muted">Leads (CRM)</div>
        <div className="text-2xl font-semibold text-muted-strong">{fmtInt(r.leads)}</div>{split('leads')}</Card>
      <Card className="p-4 flex flex-col gap-1.5"><div className="text-xs text-muted">Leads with a known source</div>
        <div className="text-2xl font-semibold text-muted-strong">{fmtPct(r.attributed_pct)}</div>
        <p className="text-[11px] text-muted leading-snug">The rest reached the clinic without an ad id or a tagged post link, so no piece can be credited.
          Piece and strategy totals count attributed leads only.</p></Card>
    </div>
  );
}

function Organic({ s, scope }: { s: Summary; scope: Scope }) {
  const o = s.organic!;
  const t: OrganicBlock = o.total;
  const fol = o.followers_gained;
  const plats = Object.entries(o.by_platform).filter(([, b]) => b.posts > 0);
  return (
    <Block tone={ORG} title="ORGANIC" subtitle="What the posts did on their own: no money spent" testid="analytics-organic">
      {t.posts === 0 ? (
        <p className="text-sm text-muted">No posted links matched yet in this range. Posted links are added on the Work card when a piece goes live.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Tile label="Views" value={fmtInt(t.views)} note={`${t.posts} posts in range`} />
          <Tile label="Post reach" value={fmtInt(t.reach)} note="each post's reach, added up" />
          <Tile label="Engagement rate" value={fmtPct(t.engagement_rate)} note="likes + comments + saves + shares, of reach" />
          <Tile label="Saves + shares" value={fmtPct(t.save_share_rate)} note="of reach" />
          <Tile label="Average watch" value={t.avg_watch_s == null ? 'not available' : `${t.avg_watch_s}s`} note="reels" />
          <Tile label="Skip rate" value={t.skip_rate == null ? 'not available' : fmtPct(t.skip_rate)} note="left in the first 3 s (reels)" />
          <Tile label="Profile visits" value={fmtInt(t.profile_visits)} note="posts and stories (not reels)" />
          <Tile label="Leads · revenue" value={`${fmtInt(t.crm_leads ?? 0)} · ${fmtMoney(t.revenue ?? 0, s.results.currency)}`} note="leads from tagged post links" />
        </div>
      )}
      <div className="text-xs text-muted">
        {scope === 'clinic' && fol
          ? <>Followers gained: {Object.entries(fol).map(([p, n]) => `${PLATFORM_LABEL[p] ?? p} ${n >= 0 ? '+' : ''}${fmtInt(n)}`).join(' · ') || 'not available'} (account level)</>
          : <>Followers: counted per account; reels give no per-post follows. Follows from feed posts and stories: {fmtInt(t.post_follows)}.</>}
      </div>
      {plats.length > 0 && (
        <Table head={['Platform', 'Posts', 'Views', 'Reach', 'Eng. rate', 'Saves', 'Shares', 'Avg watch']}
          rows={plats.map(([p, b]) => [PLATFORM_LABEL[p] ?? p, fmtInt(b.posts), fmtInt(b.views), fmtInt(b.reach), fmtPct(b.engagement_rate),
            fmtInt(b.saves), fmtInt(b.shares), b.avg_watch_s == null ? '-' : `${b.avg_watch_s}s`])} />
      )}
    </Block>
  );
}

function Paid({ s }: { s: Summary }) {
  const t: PaidBlock = s.paid!.total;
  const plats = Object.entries(s.paid!.by_platform).filter(([, b]) => b.impressions > 0 || (b.spend ?? 0) > 0);
  const results = Object.entries(t.platform_results);
  return (
    <Block tone={PAID} title="PAID" subtitle="What the ads bought, from the ad accounts" testid="analytics-paid">
      {t.impressions === 0 && !t.spend ? (
        <p className="text-sm text-muted">No ad delivery in this range{s.scope !== 'clinic' ? ' for the ads linked to it' : ''}.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {t.spend !== undefined && <Tile label="Spend" value={fmtMoney(t.spend, t.currency)} note={t.mixed_currency ? 'mixed currencies: ROAS not shown' : undefined} />}
          <Tile label="Impressions" value={fmtInt(t.impressions)} />
          <Tile label="Clicks" value={fmtInt(t.clicks)} note={`link clicks ${fmtInt(t.link_clicks)}`} />
          <Tile label="CTR" value={fmtPct(t.ctr)} note="clicks of impressions" />
          {t.cpm !== undefined && <Tile label="CPM" value={fmtMoney(t.cpm, t.currency)} note="per 1,000 impressions" />}
          {t.cpc !== undefined && <Tile label="CPC" value={fmtMoney(t.cpc, t.currency)} note="per click" />}
          <Tile label="Hook rate" value={fmtPct(t.hook_rate)} note="3-second views of impressions" />
          <Tile wrap label="Platform results" value={results.length ? results.map(([k, n]) => `${fmtInt(n)} ${RESULT_LABEL[k] ?? k}`).join(' · ') : '-'}
            note={Object.entries(t.cost_per_platform_result).map(([k, c]) => `${fmtMoney(c, t.currency)} per ${RESULT_LABEL[k] ?? k}`).join(' · ') || 'as the platforms count them'} />
          <Tile label="Leads (CRM)" value={fmtInt(t.crm_leads)} note="leads carrying an ad id" />
          {t.cost_per_lead !== undefined && <Tile label="Cost per CRM lead" value={fmtMoney(t.cost_per_lead, t.currency)} note="spend over leads carrying an ad id" />}
          <Tile label="Revenue (CRM)" value={fmtMoney(t.revenue, s.results.currency)} note="from ad leads" />
          {t.roas !== undefined && <Tile label="Return on ad spend" value={fmtX(t.roas)} note="revenue from ad leads over spend" />}
        </div>
      )}
      {plats.length > 0 && (
        <Table head={['Platform', ...(t.spend !== undefined ? ['Spend'] : []), 'Impressions', 'Clicks', 'CTR', ...(t.cpm !== undefined ? ['CPM', 'CPC'] : []), 'Hook rate', 'Leads (CRM)', ...(t.roas !== undefined ? ['ROAS'] : [])]}
          rows={plats.map(([p, b]) => [PLATFORM_LABEL[p] ?? p, ...(t.spend !== undefined ? [fmtMoney(b.spend, b.currency)] : []), fmtInt(b.impressions), fmtInt(b.clicks), fmtPct(b.ctr),
            ...(t.cpm !== undefined ? [fmtMoney(b.cpm, b.currency), fmtMoney(b.cpc, b.currency)] : []), fmtPct(b.hook_rate), fmtInt(b.crm_leads), ...(t.roas !== undefined ? [fmtX(b.roas)] : [])])} />
      )}
    </Block>
  );
}

function Pieces({ s, compact }: { s: Summary; compact?: boolean }) {
  const rows = compact ? s.pieces.slice(0, 10) : s.pieces;
  return (
    <Card className="p-4" data-testid="analytics-pieces">
      <h2 className="text-sm font-semibold text-muted-strong mb-2">Pieces, by revenue</h2>
      {rows.length === 0 ? <p className="text-sm text-muted">No piece has numbers in this range yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[920px] border-collapse">
            <thead>
              <tr className="text-[10px] font-bold tracking-wider">
                <th /><th colSpan={3} className="py-1.5 text-center border-b-2" style={{ borderColor: ORG, color: ORG }}>ORGANIC</th>
                <th colSpan={3} className="py-1.5 text-center border-b-2" style={{ borderColor: PAID, color: PAID }}>PAID</th>
                <th colSpan={3} className="py-1.5 text-center border-b-2 border-emerald-600 text-emerald-500">RESULT</th>
              </tr>
              <tr className="text-[10px] uppercase tracking-wide text-muted text-right">
                <th className="text-left py-2 pr-2 font-medium">Piece</th>
                <th className="py-2 px-2 font-medium">Views</th><th className="py-2 px-2 font-medium">Reach</th><th className="py-2 px-2 font-medium">Eng. rate</th>
                <th className="py-2 px-2 font-medium">Spend</th><th className="py-2 px-2 font-medium">CTR</th><th className="py-2 px-2 font-medium">Cost / lead</th>
                <th className="py-2 px-2 font-medium">Leads</th><th className="py-2 px-2 font-medium">Revenue</th><th className="py-2 px-2 font-medium">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-line text-right text-muted-strong">
                  <td className="text-left py-2 pr-2"><Link to={`/creatives/${p.id}`} className="hover:text-accent-400">
                    <span className="font-mono text-[11px] text-accent-400">{p.code}</span><br />{p.title}</Link></td>
                  <td className="px-2">{fmtInt(p.organic.views)}</td><td className="px-2">{fmtInt(p.organic.reach)}</td><td className="px-2">{fmtPct(p.organic.engagement_rate)}</td>
                  <td className="px-2">{p.paid.spend === undefined ? '-' : fmtMoney(p.paid.spend, s.results.currency)}</td><td className="px-2">{fmtPct(p.paid.ctr)}</td>
                  <td className="px-2">{p.paid.cost_per_lead === undefined ? '-' : fmtMoney(p.paid.cost_per_lead, s.results.currency)}</td>
                  <td className="px-2">{fmtInt(p.result.leads)}</td><td className="px-2 font-semibold text-emerald-500">{fmtMoney(p.result.revenue, s.results.currency)}</td>
                  <td className="px-2">{p.result.roas === undefined ? '-' : fmtX(p.result.roas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Plan({ plan }: { plan: NonNullable<Summary['plan']> }) {
  const label: Record<string, string> = { TOF: 'Top of funnel', MOF: 'Middle', BOF: 'Bottom' };
  return (
    <Card className="p-4" data-testid="analytics-plan">
      <h2 className="text-sm font-semibold text-muted-strong mb-3">Planned and delivered</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(plan).map(([k, v]) => (
          <div key={k} className="rounded-lg bg-bg/60 border border-line p-3">
            <div className="flex justify-between text-sm"><span className="font-medium text-muted-strong">{label[k] ?? k}</span>
              <span className="text-muted">{v.posted} of {v.planned} posted</span></div>
            <div className="h-2 mt-2 rounded bg-slate-700/30"><div className="h-2 rounded bg-slate-400" style={{ width: `${v.planned ? (v.posted / v.planned) * 100 : 0}%` }} /></div>
            <div className="text-[11px] text-muted mt-1">{v.in_production} in production</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const STATE: Record<string, string> = { ok: 'connected', expired: 'reconnect needed', error: 'error', not_connected: 'not connected' };
const KIND: Record<string, string> = {
  meta_ads: 'Meta ads', tiktok_ads: 'TikTok ads', snap_ads: 'Snapchat ads', instagram: 'Instagram', facebook_page: 'Facebook page', tiktok_account: 'TikTok organic',
};

function Sources({ s }: { s: Summary }) {
  const last = s.sources.map((x) => x.last_ok_at).filter(Boolean).sort().pop();
  return (
    <div className="text-[11px] text-muted flex flex-wrap gap-x-4 gap-y-1" data-testid="analytics-sources">
      <span>Platforms updated {last ? new Date(last).toLocaleString() : 'never'} · CRM totals {s.crm_received_at ? new Date(s.crm_received_at).toLocaleString() : 'not received yet'}.
        Platforms report up to 48 h late; the last 2 days are provisional.</span>
      {s.sources.filter((x) => x.status !== 'ok').map((x, i) => (
        <span key={i} className="text-amber-400">{KIND[x.kind] ?? x.kind}: {STATE[x.status] ?? x.status}</span>
      ))}
    </div>
  );
}

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[640px] border-collapse">
        <thead><tr className="text-[10px] uppercase tracking-wide text-muted text-right">
          {head.map((h, i) => <th key={h} className={cn('py-2 px-2 font-medium', i === 0 && 'text-left')}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t border-line text-right text-muted-strong">
            {r.map((c, j) => <td key={j} className={cn('py-2 px-2', j === 0 && 'text-left font-medium')}>{c}</td>)}</tr>))}</tbody>
      </table>
    </div>
  );
}
